import { memo, useEffect } from 'react';
import styles from './privacyPolicyPage.module.css';

export default memo(function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.metaText}>Effective Date: April 23, 2026</p>
        <p className={styles.metaText}>Last Updated: April 23, 2026</p>

        <div className={styles.body}>
          <p className={`${styles.paragraph} ${styles.intro}`}>
            This Privacy Policy describes how Autie Travel, Inc. collects, uses,
            and shares information in connection with your use of the
            autietravel.com website (the &quot;Site&quot;), a travel discovery
            and booking referral platform. The Site is intended for users age 18
            and older. By using the Site, you agree to the practices described
            in this Privacy Policy.
          </p>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>1. Who We Are</h2>
            <p className={styles.paragraph}>
              Autie Travel is operated by Autie Travel, Inc. In this Privacy
              Policy, &quot;we,&quot; &quot;us,&quot; and &quot;our&quot; refer
              to Autie Travel, Inc. as operator of the Site.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>2. Information We May Collect</h2>
            <p className={styles.paragraph}>
              Depending on how the Site operates and how you interact with it,
              we may collect the following categories of information:
            </p>

            <h3 className={styles.subTitle}>2.1 Information You Provide Directly</h3>
            <p className={styles.paragraph}>You may provide information to us when you:</p>
            <ul className={styles.list}>
              <li>contact us through the Site or by email;</li>
              <li>submit a question, inquiry, or other communication;</li>
              <li>post a comment, review, or other user-generated content;</li>
              <li>create an account or profile using your email address and a password.</li>
            </ul>
            <p className={styles.paragraph}>
              Depending on the feature involved, this information may include:
            </p>
            <ul className={styles.list}>
              <li>name or username;</li>
              <li>email address;</li>
              <li>the contents of communications sent to us;</li>
              <li>
                the contents of any comments, reviews, ratings, or other
                material you submit.
              </li>
            </ul>

            <h3 className={styles.subTitle}>2.2 Information Collected Automatically</h3>
            <p className={styles.paragraph}>
              When you visit the Site, certain information may be collected
              automatically by us or by service providers, such as:
            </p>
            <ul className={styles.list}>
              <li>IP address;</li>
              <li>browser type and device information;</li>
              <li>operating system;</li>
              <li>pages visited and time spent on pages;</li>
              <li>referral URLs;</li>
              <li>approximate location derived from IP address;</li>
              <li>dates and times of access; and</li>
              <li>technical log and diagnostic information.</li>
            </ul>
            <p className={styles.paragraph}>
              The Site does not use first-party cookies for login, session
              management, or authentication; instead, authentication tokens and
              certain other user-side data may be stored in browser
              localStorage. The Site may embed third-party functionality, such
              as mapping services, and those third parties may independently
              collect information or use cookies or similar technologies when
              users interact with their features, subject to their own privacy
              practices. The Site uses Google Analytics to measure Site traffic
              and user behavior, and this Privacy Policy reflects those
              practices.
            </p>

            <h3 className={styles.subTitle}>2.3 Booking and Referral Link Information</h3>
            <p className={styles.paragraph}>
              The Site may include links such as &ldquo;book a hotel&rdquo;
              that direct users to third-party booking services, including
              Expedia. If you click one of those links:
            </p>
            <ul className={styles.list}>
              <li>we may receive limited information about the fact that a referral occurred;</li>
              <li>the third-party platform may track that click or later booking activity;</li>
              <li>
                referral tracking may be used to determine whether Autie Travel,
                Inc. receives a commission or similar referral fee.
              </li>
            </ul>

            <h3 className={styles.subTitle}>2.4 User-Generated Content</h3>
            <p className={styles.paragraph}>
              Any information, opinions, ratings, or other content that you post
              may be visible to others. Please do not post sensitive personal
              information in comments, reviews, or public-facing fields.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>3. How We Use Information</h2>
            <p className={styles.paragraph}>
              We may use personal information and other data for the following
              business and operational purposes:
            </p>
            <ul className={styles.list}>
              <li>to provide, operate, maintain, and improve the Site;</li>
              <li>to display travel-related content and user-submitted content;</li>
              <li>to respond to inquiries, requests, or customer service communications;</li>
              <li>to enable and manage user comments, reviews, or community features;</li>
              <li>
                to detect, investigate, and address fraud, abuse, harassment,
                unlawful conduct, or violations of our Terms of Use;
              </li>
              <li>to monitor and protect the security, integrity, and functionality of the Site;</li>
              <li>to understand Site usage and performance;</li>
              <li>
                to support referral-link relationships and measure whether
                outbound links result in eligible bookings or commissions;
              </li>
              <li>
                to operate and moderate community and user-generated content
                features, including comments and reviews, for security, abuse
                prevention, content moderation, and dispute resolution purposes;
              </li>
              <li>to comply with legal obligations and enforce our rights.</li>
            </ul>
            <p className={styles.paragraph}>
              We do not send marketing or promotional emails to users.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>4. How We Share Information</h2>
            <p className={styles.paragraph}>
              We may share information in the following circumstances:
            </p>

            <h3 className={styles.subTitle}>4.1 Service Providers</h3>
            <p className={styles.paragraph}>
              We may share information with vendors or service providers that
              help us operate the Site or support business functions, such as:
            </p>
            <ul className={styles.list}>
              <li>website hosting providers;</li>
              <li>IT and security providers;</li>
              <li>analytics providers;</li>
              <li>website developers or maintenance providers;</li>
              <li>
                customer communication, form-processing, and email delivery
                providers (including email infrastructure providers such as
                Nodemailer or similar SMTP-based services used to process and
                transmit contact form submissions).
              </li>
            </ul>
            <p className={styles.paragraph}>
              These parties are permitted to use information only as reasonably
              necessary to provide services to us, subject to applicable
              contractual or legal restrictions.
            </p>

            <h3 className={styles.subTitle}>4.2 Third-Party Booking and Referral Partners</h3>
            <p className={styles.paragraph}>
              If you click an outbound link to a third-party booking service
              such as Expedia, information may be collected directly by that
              third party under its own privacy practices. We may also receive
              limited referral or performance information connected to those
              links.
            </p>
            <p className={styles.paragraph}>
              We are not responsible for the privacy practices, content,
              security, or policies of third-party websites or services.
            </p>

            <h3 className={styles.subTitle}>4.3 Legal Compliance and Protection</h3>
            <p className={styles.paragraph}>
              We may disclose information if we believe in good faith that doing
              so is appropriate to:
            </p>
            <ul className={styles.list}>
              <li>comply with applicable law, regulation, legal process, or governmental request;</li>
              <li>enforce our Terms of Use or other policies;</li>
              <li>investigate potential violations, abuse, or unlawful conduct;</li>
              <li>
                protect the rights, safety, or property of Autie Travel, the
                Site, users, or others.
              </li>
            </ul>

            <h3 className={styles.subTitle}>4.4 Business Transfers</h3>
            <p className={styles.paragraph}>
              We may disclose or transfer information as part of a proposed or
              completed merger, acquisition, financing, sale of assets,
              restructuring, or similar business transaction. Any successor or
              acquiring party will be expected to handle personal information in
              a manner consistent with this Privacy Policy or will provide
              notice of any material changes in accordance with Section 13.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>5. Third-Party Links and Services</h2>
            <p className={styles.paragraph}>
              The Site may link to third-party websites, services, and
              platforms, including Expedia, and may in the future contain other
              integrations or embedded content. This Privacy Policy applies only
              to the Site itself and not to third-party properties.
            </p>
            <p className={styles.paragraph}>
              If you leave the Site or interact with third-party tools, your
              information may be collected and handled under those third
              parties&apos; own terms and privacy policies. We encourage users
              to review those policies before providing information or
              completing transactions.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>6. Cookies and Similar Technologies</h2>
            <p className={styles.paragraph}>
              The Site does not use first-party cookies for login, session
              management, or authentication. Authentication tokens and certain
              other user-side data may be stored in browser localStorage rather
              than cookies. The following tracking technologies may be present
              on or in connection with the Site:
            </p>
            <ul className={styles.list}>
              <li>
                essential technical tools required for Site functionality and
                security, which may include localStorage or similar browser
                storage mechanisms;
              </li>
              <li>
                analytics technologies, including Google Analytics, which the
                Site uses to collect information about Site usage and user
                behavior. Google Analytics may use cookies and similar
                technologies in connection with this measurement. For
                information about how Google uses data collected through its
                services, please visit{' '}
                <a
                  className={styles.link}
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  https://policies.google.com/privacy
                </a>
                . Users may opt out of Google Analytics measurement by using the
                Google Analytics Opt-out Browser Add-on available at{' '}
                <a
                  className={styles.link}
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  https://tools.google.com/dlpage/gaoptout
                </a>
                ;
              </li>
              <li>
                tracking technologies independently operated by Google in
                connection with embedded Google Maps functionality on the Site.
                When you interact with map features, Google may collect and
                process information subject to Google&apos;s Privacy Policy and
                Terms of Service, over which we have no control.
              </li>
            </ul>
            <p className={styles.paragraph}>
              Most browsers allow users to manage cookies and browser storage
              (including localStorage) through browser settings. Blocking or
              clearing certain technologies may affect the functionality of
              embedded features such as Google Maps or other Site features that
              rely on browser storage.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>7. California Privacy Disclosures</h2>

            <h3 className={styles.subTitle}>7.1 Categories of Information Collected</h3>
            <p className={styles.paragraph}>
              Depending on actual Site use, we may collect the following
              categories of personal information from California users:
            </p>
            <ul className={styles.list}>
              <li>Identifiers: Name, email address, IP address, username</li>
              <li>
                Internet or network activity: Browsing activity, pages viewed,
                referral data, technical logs
              </li>
              <li>Geolocation data: Approximate location derived from IP address</li>
              <li>
                User submissions: Comments, reviews, ratings, messages, other
                content you provide
              </li>
              <li>
                Inferences: General preferences or interests inferred from Site
                interactions, based on analytics and other measurement tools
                used by the Site.
              </li>
            </ul>

            <h3 className={styles.subTitle}>7.2 Sources of Information</h3>
            <p className={styles.paragraph}>We may collect information:</p>
            <ul className={styles.list}>
              <li>directly from users;</li>
              <li>automatically from devices and browsers;</li>
              <li>from service providers supporting the Site;</li>
              <li>from third-party partners involved in referral or booking link tracking.</li>
            </ul>

            <h3 className={styles.subTitle}>7.3 Business Purposes for Collection</h3>
            <p className={styles.paragraph}>
              We may collect and use personal information for the purposes
              described in Section 3 above, including Site operation, security,
              communications, content moderation, performance measurement,
              referral tracking, and legal compliance. For information about how
              long we retain each category of personal information, please see
              Section 9 (Data Retention) below.
            </p>

            <h3 className={styles.subTitle}>7.4 Sharing and Disclosure</h3>
            <p className={styles.paragraph}>
              We may disclose personal information to service providers,
              referral partners, legal authorities, and transaction
              counterparties as described in this Privacy Policy.
            </p>

            <h3 className={styles.subTitle}>7.5 Sale or Sharing of Personal Information</h3>
            <p className={styles.paragraph}>
              We do not sell personal information for money, and we do not share
              personal information for cross-context behavioral advertising
              purposes. The Site may use third-party outbound links and may use
              technologies that involve limited data sharing related to referral
              tracking or site measurement. If the Site later uses advertising,
              retargeting, or more advanced tracking technologies that
              constitute a &quot;sale&quot; or &quot;sharing&quot; under
              California law, this Privacy Policy and any required California
              disclosures will be updated accordingly.
            </p>

            <h3 className={styles.subTitle}>7.6 California Privacy Requests</h3>
            <p className={styles.paragraph}>
              California users may contact us to request information about our
              privacy practices or to request access, correction, or deletion of
              personal information, as applicable and subject to verification,
              exceptions, and legal limitations. We will not discriminate
              against you for exercising your California privacy rights.
            </p>
            <p className={styles.paragraph}>
              To submit a request, please use the contact information in
              Section 12 below. We may need to verify your identity before
              processing certain requests. We may deny or limit a request where
              permitted by law, including where retention is required for
              security, legal compliance, recordkeeping, or internal operational
              purposes.
            </p>

            <h3 className={styles.subTitle}>7.7 California &ldquo;Shine the Light&rdquo;</h3>
            <p className={styles.paragraph}>
              California Civil Code section 1798.83 permits California residents
              to request information about personal information shared with
              third parties for those third parties&apos; direct marketing
              purposes. Autie Travel, Inc. does not share personal information
              with third parties for their own direct marketing purposes and
              therefore is not required to maintain or provide such a
              disclosure. If our practices change, this Privacy Policy will be
              updated accordingly.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>8. Data Security</h2>
            <p className={styles.paragraph}>
              We use reasonable administrative, technical, and organizational
              measures designed to help protect information under our control.
              However:
            </p>
            <p className={styles.paragraph}>
              Where users create accounts with passwords, we use
              industry-standard methods to store passwords securely, including
              hashing using appropriate cryptographic techniques. Plaintext
              passwords are not stored by us. We periodically review our
              security practices to ensure they remain consistent with current
              standards.
            </p>
            <ul className={styles.list}>
              <li>no website, system, or transmission method is completely secure;</li>
              <li>we cannot guarantee absolute security of information transmitted to or from the Site; and</li>
              <li>
                we are not responsible for the security practices of third-party
                websites, platforms, or services to which the Site may link.
              </li>
            </ul>
            <p className={styles.paragraph}>
              Users should avoid sending highly sensitive information through
              general website forms, comments, or other public-facing features.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>9. Data Retention</h2>
            <p className={styles.paragraph}>
              We retain personal information for as long as reasonably necessary
              to operate the Site, fulfill the purposes described in this
              Privacy Policy, and comply with applicable legal obligations. We
              do not operate automated data deletion schedules. Deletion of
              personal information is carried out at our discretion, subject to
              applicable legal requirements. We may, but are not obligated to,
              delete or anonymize certain information at our discretion.
            </p>
            <p className={styles.paragraph}>
              Different categories of information may be retained for different
              periods depending on the nature of the information and the purpose
              for which it was collected. For example:
            </p>
            <ul className={styles.list}>
              <li>
                account information and associated data are generally retained
                until you request deletion of your account or we otherwise
                determine in our sole discretion that retention is no longer
                necessary; upon account closure or deactivation, we may, but are
                not obligated to, delete or anonymize certain account-related
                information, except where retention is necessary for legal,
                security, fraud-prevention, recordkeeping, or dispute-resolution
                purposes, or where we otherwise determine in our sole discretion
                that retention serves a legitimate operational interest;
              </li>
              <li>
                comments, reviews, and other user-generated content may remain
                available for as long as relevant to the operation of the Site,
                unless removed by us or the user, although copies may persist in
                backups, archives, or systems outside our control;
              </li>
              <li>
                technical logs and analytics-related information may be retained
                for a shorter or longer period depending on operational needs
                and system integrity purposes; and
              </li>
              <li>
                referral, booking-related, and communications records may be
                retained as needed for business, tax, accounting, legal, and
                enforcement purposes.
              </li>
            </ul>
            <p className={styles.paragraph}>
              We may also retain information for a longer period where required
              or permitted by applicable law, or where necessary to investigate
              misconduct, protect our rights, or comply with legal process.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>10. Children&apos;s Privacy; 18+ Use</h2>
            <p className={styles.paragraph}>
              The Site is intended for adults age 18 and older. We do not direct
              the Site to children under 18, and we do not knowingly collect
              personal information from users under 18. If we learn that we have
              collected personal information from a user under 18, we will take
              prompt steps to delete that information.
            </p>
            <p className={styles.paragraph}>
              If you believe a person under 18 has provided personal information
              to us, please contact us so we can review the issue and take
              appropriate action.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>11. User Rights, Choices, and Public Content</h2>
            <p className={styles.paragraph}>
              Subject to applicable law and verification requirements, users may
              contact us to:
            </p>
            <ul className={styles.list}>
              <li>request access to certain personal information we hold about them;</li>
              <li>request correction of inaccurate personal information;</li>
              <li>request deletion of personal information, where applicable;</li>
              <li>ask questions about this Privacy Policy or our data practices;</li>
              <li>request review or removal of comments or reviews, where appropriate.</li>
            </ul>
            <p className={styles.paragraph}>
              We reserve the right to refuse, limit, or delay requests where
              permitted by law, where we cannot verify the request, or where
              retention or continued processing is reasonably necessary for
              legal, security, moderation, operational, or evidentiary purposes.
            </p>
            <p className={styles.paragraph}>
              If you post comments, reviews, or other public content, that
              content may remain visible to others even after a removal request
              in some circumstances, such as where copies, caches, screenshots,
              reposts, or archival versions exist outside our control.
            </p>
            <p className={styles.paragraph}>
              To submit a data deletion request, please contact us using the
              contact information provided in Section 12 below. In your request,
              please include your name, the email address associated with your
              account (if applicable), and a description of the information you
              are requesting be deleted. We may need to verify your identity
              before processing your request. We will endeavor to respond to
              verified requests within a reasonable time, subject to applicable
              legal requirements and the limitations described in this Privacy
              Policy. Please note that we may retain certain information even
              following a deletion request where retention is required or
              permitted by applicable law, or where necessary for legal
              compliance, security, fraud prevention, recordkeeping, dispute
              resolution, or other legitimate operational purposes. We are not
              obligated to delete information that we are permitted to retain,
              and deletion of certain information may affect your ability to use
              certain features of the Site. California users have additional
              rights with respect to deletion requests as described in Section
              7.6 above.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>12. Contact Us</h2>
            <p className={styles.paragraph}>
              If you have questions about this Privacy Policy or wish to submit
              a privacy-related request, please contact:
            </p>
            <div className={styles.contactBlock}>
              <p className={styles.paragraph}>
                <strong>Autie Travel, Inc.</strong>
                <br />
                Attn: Privacy Inquiries
                <br />
                31 Corte Morada, Greenbrae, CA 94904
                <br />
                <a className={styles.link} href="mailto:sam@autietravel.com">
                  sam@autietravel.com
                </a>
              </p>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>13. Changes to This Privacy Policy</h2>
            <p className={styles.paragraph}>
              We may revise this Privacy Policy from time to time to reflect
              changes in the Site, legal requirements, business practices, or
              third-party services. When we do, we will update the &quot;Last
              Updated&quot; date above and, where appropriate, provide
              additional notice. For material changes, we will endeavor to
              provide reasonable advance notice before such changes become
              effective, except where a shorter period is required by law or
              necessary circumstance. Your continued use of the Site after an
              updated Privacy Policy becomes effective means you acknowledge the
              revised policy, to the extent permitted by law.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
});
